import googleLogo from "../../img/publishers/google.png";
import facebookLogo from "../../img/publishers/facebook.png";
import bingLogo from "../../img/publishers/bing.png";
import yelpLogo from "../../img/publishers/yelp.png";
import mapquestLogo from "../../img/publishers/mapquest.png";
import foursquareLogo from "../../img/publishers/foursquare.png";
import opentableLogo from "../../img/publishers/opentable.png";
import "./PublisherLogo.scss";

type PublisherKey =
  | "google"
  | "facebook"
  | "bing"
  | "yelp"
  | "mapquest"
  | "foursquare"
  | "opentable";

// Maps a Yext publisher id/name to a styled logo key. Unmatched publishers fall back to a
// generic initial badge (keyed null), so any directory Yext returns still renders.
function publisherKey(idOrName: string): PublisherKey | null {
  const v = idOrName.toLowerCase();
  if (v.includes("google")) return "google";
  if (v.includes("facebook")) return "facebook";
  if (v.includes("bing")) return "bing";
  if (v.includes("yelp")) return "yelp";
  if (v.includes("mapquest")) return "mapquest";
  if (v.includes("foursquare")) return "foursquare";
  if (v.includes("opentable") || v.includes("open table")) return "opentable";
  return null;
}

interface Props {
  // A known publisher key, or any publisher name to be matched/fallback-rendered.
  publisher: PublisherKey | string;
}

// Real logo assets, keyed by publisher — one per known publisher.
const LOGO_IMG: Record<PublisherKey, string> = {
  google: googleLogo,
  facebook: facebookLogo,
  bing: bingLogo,
  yelp: yelpLogo,
  mapquest: mapquestLogo,
  foursquare: foursquareLogo,
  opentable: opentableLogo,
};

// Logos that are full circular badges fill the frame; the rest sit centered on a white tile.
const FILL_LOGOS = new Set<PublisherKey>(["facebook", "foursquare", "opentable"]);

export function PublisherLogo({ publisher }: Props) {
  const key = isKnownKey(publisher) ? publisher : publisherKey(publisher);
  const img = key ? LOGO_IMG[key] : undefined;
  if (img && key) {
    return (
      <span className={`publisher-logo publisher-logo-img${FILL_LOGOS.has(key) ? " fill" : ""}`}>
        <img className="pl-img" src={img} alt={key} />
      </span>
    );
  }
  // Fallback: first initial on a neutral badge (unknown publisher, or a known key awaiting artwork).
  const initial = publisher.trim().charAt(0).toUpperCase() || "?";
  return (
    <span className="publisher-logo publisher-logo-generic">
      <span className="pl-mono pl-mono-small">{initial}</span>
    </span>
  );
}

const KNOWN_KEYS: PublisherKey[] = [
  "google",
  "facebook",
  "bing",
  "yelp",
  "mapquest",
  "foursquare",
  "opentable",
];

function isKnownKey(v: string): v is PublisherKey {
  return (KNOWN_KEYS as string[]).includes(v);
}
